/**
 * DecisionFlow — CompDispute
 * ui/src/components/admin/CompDispute.jsx
 * Stage 11 — Compliance Lifecycle UI
 *
 * Disputes panel for comp_admin. Route: /admin/disputes
 * Backend: disputes_router.py (Stage 8 — live)
 * API contract: §18.1–18.4 (v1.11 — authoritative)
 *
 * D1: Slide-out drawer for detail + actions
 * D2: Modal form for create
 * D3: Appended to Execution group in CompAdminShell nav
 *
 * Lifecycle: submitted → under_review → escalated → resolved → closed
 * NN-22: financial_impact required before close — UI enforces + server enforces
 * SLA breach: sla_breached=true → visual alert on row (no extra API call)
 * CC-1: comp_admin only for detail/patch — finance has separate read-only panel in FinanceShell
 * DD-06: all data from live API
 * Accessibility §0.4.2: all pills use color + icon
 */

import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle, X, Send, Eye, ArrowUp, CheckCircle, Lock,
  AlertTriangle, Plus,
} from "lucide-react";
import Card from "../shared/Card.jsx";
import Field from "../shared/Field.jsx";
import Pill from "../shared/Pill.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";


function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    fetch(url, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

function LoadingRow() {
  return (
    <div className="space-y-2">
      <div className="skeleton h-4 w-2/3 rounded " />
      <div className="skeleton h-4 w-1/2 rounded " />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-sm text-[rgb(var(--color-danger))] ">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill helpers — color + icon per §0.4.2 (no info by color alone)
// ---------------------------------------------------------------------------

const DISPUTE_STATUS_CONFIG = {
  submitted:    { tone: "Draft",    Icon: Send,        label: "Submitted"    },
  under_review: { tone: "Ready",    Icon: Eye,         label: "Under Review" },
  escalated:    { tone: "Blocked",  Icon: ArrowUp,     label: "Escalated"    },
  resolved:     { tone: "Complete", Icon: CheckCircle, label: "Resolved"     },
  closed:       { tone: "Draft",    Icon: Lock,        label: "Closed"       },
};

const DISPUTE_TYPE_LABELS = {
  // Core dispute types (original values)
  territory_credit:          "Territory Credit",
  system_error:              "System Error",
  mbo_ambiguity:             "MBO Ambiguity",
  fx_rate:                   "FX Rate",
  eligibility:               "Eligibility",
  other:                     "Other",
  // Modifier domain dispute types (spec_exception_dispute_engine_v2_0.md, m128)
  kicker_dispute:            "Kicker Dispute",
  draw_dispute:              "Draw Dispute",
  split_dispute:             "Split Dispute",
  spiff_dispute:             "Spiff Dispute",
  transaction_dispute:       "Transaction Dispute",
  clawback_dispute:          "Clawback Dispute",
  revenue_type_rate_dispute: "Revenue Type Rate Dispute",
};

// Modifier types requiring a linkage field in the create modal (spec §8.3)
// Maps dispute_type → { field: payload key, label: UI label }
const MODIFIER_DISPUTE_LINKAGE = {
  kicker_dispute:            { field: "kicker_plan_id",             label: "Kicker Plan ID" },
  draw_dispute:              { field: "draw_plan_id",               label: "Draw Plan ID" },
  split_dispute:             { field: "split_plan_id",              label: "Split Plan ID" },
  spiff_dispute:             { field: "spiff_plan_id",              label: "Spiff Plan ID" },
  transaction_dispute:       { field: "transaction_plan_id",        label: "Transaction Plan ID" },
  clawback_dispute:          { field: "clawback_policy_id",         label: "Clawback Policy ID" },
  revenue_type_rate_dispute: { field: "revenue_type_rate_table_id", label: "Revenue Type Rate Table ID" },
};

function StatusPill({ status }) {
  const cfg = DISPUTE_STATUS_CONFIG[status] ?? { tone: "Draft", Icon: AlertCircle, label: status };
  return (
    <Pill tone={cfg.tone}>
      <span className="flex items-center gap-1">
        <cfg.Icon className="h-3 w-3" />
        {cfg.label}
      </span>
    </Pill>
  );
}

function TypePill({ type }) {
  return <Pill tone="Draft">{DISPUTE_TYPE_LABELS[type] ?? type}</Pill>;
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function fmt(val) {
  if (val == null || val === "") return "—";
  return val;
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString();
}

function fmtAmount(amount, currency) {
  if (amount == null) return "—";
  return `${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? " " + currency.trim() : ""}`;
}

// ---------------------------------------------------------------------------
// Create Modal (D2)
// ---------------------------------------------------------------------------

function CreateDisputeModal({ open, onClose, onCreated, period }) {
  const [form, setForm] = useState({
    dispute_type:         "",
    dispute_description:  "",
    position_id:          "",
    fiscal_period_id:     period?.fiscal_period_id ?? "",
    payout_summary_id:    "",
    financial_impact:     "",
    impact_currency_code: "",
    modifier_plan_id:     "",  // mapped to correct linkage field on submit (spec §8.3)
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = periodsData?.fiscal_periods ?? periodsData?.data ?? [];
  const periodLabelById = Object.fromEntries(periods.map((p) => [String(p.fiscal_period_id), p.period_label ?? p.period_code]));  // OI-S209-06

  useEffect(() => {
    if (period?.fiscal_period_id) {
      setForm((f) => ({ ...f, fiscal_period_id: period.fiscal_period_id }));
    }
  }, [period]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit() {
    if (!form.dispute_type || !form.dispute_description.trim() || !form.position_id.trim() || !form.fiscal_period_id) {
      setError("Dispute type, description, position ID, and fiscal period are required.");
      return;
    }
    // Modifier linkage validation: router enforces 422 MODIFIER_LINKAGE_REQUIRED (spec §8.3)
    const linkageCfg = MODIFIER_DISPUTE_LINKAGE[form.dispute_type];
    if (linkageCfg && !form.modifier_plan_id.trim()) {
      setError(`${linkageCfg.label} is required for ${DISPUTE_TYPE_LABELS[form.dispute_type]} disputes.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        dispute_type:        form.dispute_type,
        dispute_description: form.dispute_description.trim(),
        position_id:         form.position_id.trim(),
        fiscal_period_id:    Number(form.fiscal_period_id),
      };
      if (form.payout_summary_id.trim())    payload.payout_summary_id    = form.payout_summary_id.trim();
      if (form.financial_impact !== "")     payload.financial_impact     = Number(form.financial_impact);
      if (form.impact_currency_code.trim()) payload.impact_currency_code = form.impact_currency_code.trim().toUpperCase();
      // Map modifier_plan_id to correct linkage field for this dispute type
      if (linkageCfg && form.modifier_plan_id.trim()) {
        payload[linkageCfg.field] = Number(form.modifier_plan_id.trim());
      }

      const res = await fetch(`${API_BASE}/disputes`, {
        method:  "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgb(var(--color-text))]/30 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="w-full max-w-lg rounded-2xl bg-[rgb(var(--color-surface))] p-6 shadow-xl "
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-[rgb(var(--color-text))] ">
            New Dispute
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] "
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Dispute Type */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Dispute Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.dispute_type}
              onChange={(e) => set("dispute_type", e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            >
              <option value="">Select type…</option>
              {Object.entries(DISPUTE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Modifier Linkage Field — conditional on dispute type (spec §8.3) */}
          {MODIFIER_DISPUTE_LINKAGE[form.dispute_type] && (
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                {MODIFIER_DISPUTE_LINKAGE[form.dispute_type].label} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.modifier_plan_id}
                onChange={(e) => set("modifier_plan_id", e.target.value)}
                placeholder="Integer ID of the linked plan"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.dispute_description}
              onChange={(e) => set("dispute_description", e.target.value)}
              rows={3}
              placeholder="Describe the dispute…"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none resize-none"
            />
          </div>

          {/* Position ID */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Position ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.position_id}
              onChange={(e) => set("position_id", e.target.value)}
              placeholder="UUID of affected position"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            />
          </div>

          {/* Fiscal Period */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Fiscal Period <span className="text-red-500">*</span>
            </label>
            <select
              value={form.fiscal_period_id}
              onChange={(e) => set("fiscal_period_id", e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            >
              <option value="">Select period…</option>
              {periods.map((p) => (
                <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                  {p.period_label ?? p.period_code}
                </option>
              ))}
            </select>
          </div>

          {/* Payout Summary ID (optional) */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Payout Summary ID (optional)
            </label>
            <input
              type="text"
              value={form.payout_summary_id}
              onChange={(e) => set("payout_summary_id", e.target.value)}
              placeholder="UUID — links dispute to a specific payout"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            />
          </div>

          {/* Financial Impact (optional) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                Financial Impact
              </label>
              <input
                type="number"
                value={form.financial_impact}
                onChange={(e) => set("financial_impact", e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                Currency
              </label>
              <input
                type="text"
                value={form.impact_currency_code}
                onChange={(e) => set("impact_currency_code", e.target.value)}
                placeholder="USD"
                maxLength={3}
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
          </div>

          {error && <ErrorNote message={error} />}

          <div className="flex gap-2 pt-1">
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Creating…" : "Create Dispute"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail Drawer (D1) — slide-out from right
// Actions gated by dispute_status per spec §3.5
// NN-22: financial_impact required on close — enforced here + server-side
// ---------------------------------------------------------------------------

function DisputeDrawer({ dispute, open, onClose, onUpdated }) {
  const [actionMode, setActionMode]         = useState(null); // "resolve" | "close"
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [financialImpact, setFinancialImpact] = useState("");
  const [impactCurrency, setImpactCurrency] = useState("");
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState(null);

  useEffect(() => {
    setActionMode(null);
    setResolutionNotes("");
    setFinancialImpact("");
    setImpactCurrency("");
    setError(null);
  }, [dispute?.dispute_id]);

  if (!dispute) return null;

  async function patch(payload) {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/disputes/${dispute.dispute_id}`, {
        method:  "PATCH",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMarkUnderReview() {
    await patch({ dispute_status: "under_review" });
  }

  async function handleEscalate() {
    await patch({ dispute_status: "escalated" });
  }

  async function handleResolve() {
    if (!resolutionNotes.trim()) {
      setError("Resolution notes are required.");
      return;
    }
    await patch({ dispute_status: "resolved", resolution_notes: resolutionNotes.trim() });
  }

  async function handleClose() {
    // NN-22: financial_impact required before close
    if (!financialImpact || isNaN(Number(financialImpact))) {
      setError("Financial impact is required to close a dispute (NN-22).");
      return;
    }
    const payload = {
      dispute_status:   "closed",
      financial_impact: Number(financialImpact),
    };
    if (impactCurrency.trim()) {
      payload.impact_currency_code = impactCurrency.trim().toUpperCase();
    }
    await patch(payload);
  }

  const status = dispute.dispute_status;

  return (
    <AnimatePresence>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-[rgb(var(--color-text))]/20"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-xl  "
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 ">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                <span className="text-sm font-semibold text-[rgb(var(--color-text))] ">
                  Dispute Detail
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TypePill type={dispute.dispute_type} />
                <StatusPill status={dispute.dispute_status} />
                {dispute.sla_breached && (
                  <span className="flex items-center gap-0.5 text-xs font-medium text-red-600 ">
                    <AlertTriangle className="h-3 w-3" /> SLA Breached
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="ml-2 rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] "
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

              {/* Core fields */}
              <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-3 ">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Dispute Type" value={DISPUTE_TYPE_LABELS[dispute.dispute_type] ?? dispute.dispute_type} />
                  <Field label="Status"       value={dispute.dispute_status?.replace(/_/g, " ")} />
                </div>
                <Field label="Description" value={fmt(dispute.dispute_description)} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Position ID"   value={fmt(dispute.position_id)} />
                  <Field label="Fiscal Period" value={periodLabelById[String(dispute.fiscal_period_id)] ?? fmt(dispute.fiscal_period_id)} />  {/* OI-S209-06 */}
                </div>
                {dispute.payout_summary_id && (
                  <Field label="Payout Summary" value={dispute.payout_summary_id} />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Submitted By" value={fmt(dispute.submitted_by)} />
                  <Field label="Submitted At" value={fmtDate(dispute.submitted_at)} />
                </div>
                {dispute.assigned_to && (
                  <Field label="Assigned To" value={dispute.assigned_to} />
                )}
                {/* Modifier linkage field — shown when present (spec §8.3, v2_0 §3.2) */}
                {MODIFIER_DISPUTE_LINKAGE[dispute.dispute_type] && (() => {
                  const cfg   = MODIFIER_DISPUTE_LINKAGE[dispute.dispute_type];
                  const value = dispute[cfg.field];
                  return value != null ? (
                    <Field label={cfg.label} value={fmt(String(value))} />
                  ) : null;
                })()}
              </div>

              {/* SLA block */}
              {dispute.sla_target_at && (
                <div className={`rounded-xl border px-4 py-3 space-y-2 ${dispute.sla_breached ? "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10  " : "border-[rgb(var(--color-border))] "}`}>
                  <p className={`text-xs font-semibold ${dispute.sla_breached ? "text-[rgb(var(--color-danger))] " : "text-[rgb(var(--color-text-muted))]"}`}>
                    SLA {dispute.sla_breached ? "— BREACHED" : ""}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="SLA Target"  value={fmtDate(dispute.sla_target_at)} />
                    <Field label="SLA Breached" value={dispute.sla_breached ? "Yes" : "No"} />
                  </div>
                </div>
              )}

              {/* Escalation block */}
              {dispute.escalated_to && (
                <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-4 py-3 space-y-2  ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-warning))] ">Escalation</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Escalated To" value={fmt(dispute.escalated_to)} />
                    <Field label="Escalated At" value={fmtDate(dispute.escalated_at)} />
                  </div>
                </div>
              )}

              {/* Resolution block */}
              {dispute.resolved_by && (
                <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 px-4 py-3 space-y-2  ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-success))] ">Resolution</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Resolved By" value={fmt(dispute.resolved_by)} />
                    <Field label="Resolved At" value={fmtDate(dispute.resolved_at)} />
                  </div>
                  <Field label="Resolution Notes" value={fmt(dispute.resolution_notes)} />
                </div>
              )}

              {/* Financial / Close block */}
              {(dispute.financial_impact != null || dispute.closed_by) && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-2 ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-text-muted))]">Financial / Close</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Financial Impact" value={fmtAmount(dispute.financial_impact, dispute.impact_currency_code)} />
                    <Field label="Closed By"        value={fmt(dispute.closed_by)} />
                  </div>
                  <Field label="Closed At" value={fmtDate(dispute.closed_at)} />
                </div>
              )}

              {error && <ErrorNote message={error} />}

              {/* Actions — gated by dispute_status (spec §3.5) */}
              {status === "submitted" && (
                <Button onClick={handleMarkUnderReview} disabled={submitting}>
                  <Eye className="h-4 w-4" />
                  {submitting ? "Updating…" : "Mark Under Review"}
                </Button>
              )}

              {(status === "under_review" || status === "escalated") && (
                <div className="space-y-2">
                  {status === "under_review" && (
                    <Button variant="secondary" onClick={handleEscalate} disabled={submitting || actionMode === "resolve"}>
                      <ArrowUp className="h-4 w-4" />
                      Escalate
                    </Button>
                  )}
                  {actionMode !== "resolve" ? (
                    <Button onClick={() => { setActionMode("resolve"); setError(null); }} disabled={submitting}>
                      <CheckCircle className="h-4 w-4" />
                      Resolve
                    </Button>
                  ) : (
                    <div className="space-y-2 rounded-xl border border-[rgb(var(--color-success))]/30 p-3 ">
                      <label className="block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Resolution Notes <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        rows={3}
                        placeholder="Required — describe the resolution…"
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleResolve}
                          disabled={submitting || !resolutionNotes.trim()}
                        >
                          {submitting ? "Resolving…" : "Confirm Resolve"}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => { setActionMode(null); setError(null); }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Close — requires financial_impact (NN-22) */}
              {status === "resolved" && (
                <>
                  {actionMode !== "close" ? (
                    <Button onClick={() => { setActionMode("close"); setError(null); }} disabled={submitting}>
                      <Lock className="h-4 w-4" />
                      Close Dispute
                    </Button>
                  ) : (
                    <div className="space-y-2 rounded-xl border border-[rgb(var(--color-border))] p-3 ">
                      <p className="text-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Financial impact required to close (NN-22)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                            Financial Impact <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            value={financialImpact}
                            onChange={(e) => setFinancialImpact(e.target.value)}
                            placeholder="0.00"
                            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                            Currency
                          </label>
                          <input
                            type="text"
                            value={impactCurrency}
                            onChange={(e) => setImpactCurrency(e.target.value)}
                            placeholder="USD"
                            maxLength={3}
                            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleClose}
                          disabled={submitting || !financialImpact}
                        >
                          {submitting ? "Closing…" : "Confirm Close"}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => { setActionMode(null); setError(null); }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// CompDispute — main panel
// ---------------------------------------------------------------------------

export default function CompDispute({ period }) {
  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  const isLocked = period?.period_status !== "open";

  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType,   setFilterType]   = useState("");

  function fetchDisputes() {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/disputes`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setDisputes(d?.data ?? d?.disputes ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchDisputes(); }, []);

  const selectedRecord = disputes.find((d) => d.dispute_id === selectedId) ?? null;

  function openDrawer(dispute) {
    setSelectedId(dispute.dispute_id);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setSelectedId(null);
  }

  function handleCreated() {
    setModalOpen(false);
    fetchDisputes();
  }

  function handleUpdated() {
    closeDrawer();
    fetchDisputes();
  }

  const filtered = disputes.filter((d) => {
    if (filterStatus && d.dispute_status !== filterStatus) return false;
    if (filterType   && d.dispute_type   !== filterType)   return false;
    return true;
  });

  return (
    <>
      <div className="w-full">
        <Card
          title="Disputes"
          action={
            isLocked ? (
              <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5">
                <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
                <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status}</span>
              </div>
            ) : (
              <Button onClick={() => setModalOpen(true)}>
                <Plus className="h-4 w-4" />
                New Dispute
              </Button>
            )
          }
        >
          {/* Filters */}
          <div className="mb-3 flex flex-wrap gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-xs   focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-xs   focus:outline-none"
            >
              <option value="">All Types</option>
              {Object.entries(DISPUTE_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {loading && <LoadingRow />}
          {error   && <ErrorNote message="Unable to load disputes." />}

          {!loading && !error && filtered.length === 0 && (
            <p className="text-sm text-[rgb(var(--color-text-muted))]">
              {disputes.length === 0
                ? "No disputes found for this tenant."
                : "No disputes match the selected filters."}
            </p>
          )}

          <div className="space-y-2">
            {filtered.map((dispute) => (
              <button
                key={dispute.dispute_id}
                onClick={() => openDrawer(dispute)}
                className="w-full text-left rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5 transition hover:border-[rgb(var(--color-border-strong))] hover:bg-[rgb(var(--color-surface-muted))]  "
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="grid flex-1 gap-1 sm:grid-cols-3">
                    <Field label="Type"      value={DISPUTE_TYPE_LABELS[dispute.dispute_type] ?? dispute.dispute_type} />
                    <Field label="Period"    value={periodLabelById[String(dispute.fiscal_period_id)] ?? fmt(dispute.fiscal_period_id)} />  {/* OI-S209-06 */}
                    <Field label="Submitted" value={fmtDate(dispute.submitted_at)} />
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <StatusPill status={dispute.dispute_status} />
                    {dispute.sla_breached && (
                      <span className="flex items-center gap-0.5 text-xs text-red-600 ">
                        <AlertTriangle className="h-3 w-3" /> SLA Breached
                      </span>
                    )}
                  </div>
                </div>
                {dispute.dispute_description && (
                  <p className="mt-1.5 truncate text-xs text-[rgb(var(--color-text-muted))]">
                    {dispute.dispute_description.length > 60
                      ? dispute.dispute_description.slice(0, 60) + "…"
                      : dispute.dispute_description}
                  </p>
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {modalOpen && (
          <CreateDisputeModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onCreated={handleCreated}
            period={period}
          />
        )}
      </AnimatePresence>

      {/* Detail Drawer */}
      <DisputeDrawer
        dispute={selectedRecord}
        open={drawerOpen}
        onClose={closeDrawer}
        onUpdated={handleUpdated}
      />
    </>
  );
}
